import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    PackageMinus, Building2, Search, Plus, Trash2, X, RefreshCw, Send,
    ChevronDown, Loader2, Layers,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchCostCenters,
    fetchCategories,
    fetchMainGrid,
    fetchSuggestions,
    fetchLotDetails,
    addTempIssueItems,
    deleteIssueItem,
    submitDailyIssue,
    clearMainGrid,
    clearSuggestions,
    clearLotDetails,
    clearAddTempResult,
    clearSubmitResult,
    resetDailyIssue,
    selectDICCList,
    selectDICategoryList,
    selectDIMainGridItems,
    selectDISuggestions,
    selectDILotDetails,
    selectDIAddTempResult,
    selectDISubmitResult,
    selectDILoading,
    selectDIErrors,
} from '../../slices/purchaseSlice/dailyIssueSlice';


// Date picker values are JS Dates; sent raw they serialise as UTC ("…T18:30:00Z" = previous day in IST).
// Send the local calendar date instead; strings (already formatted) pass through unchanged.
const toApiDate = (v) => {
    if (!v) return '';
    if (v instanceof Date) {
        return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
    }
    return v;
};

// ── Shared UI primitives (mirrors IndentAmend / SupplierPOAmend pages) ──

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo' }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
        purple: 'from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 text-purple-800 dark:text-purple-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <h3 className="font-semibold text-sm">{title}</h3>
                {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

const todayFormatted = () => {
    const d = new Date();
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const SELECTION_TYPES = [
    { value: '1', label: 'Search By Item Code' },
    { value: '2', label: 'Search By Item Name' },
];

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

const DailyIssue = () => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userDetails?.userName || 'system';
    const roleId = userData?.roleId || userData?.RID || 0;
    const uid = userData?.UID || userData?.uid || '';

    const ccList       = useSelector(selectDICCList);
    const categoryList = useSelector(selectDICategoryList);
    const mainGridItems= useSelector(selectDIMainGridItems);
    const suggestions  = useSelector(selectDISuggestions);
    const lotDetails   = useSelector(selectDILotDetails);
    const addTempResult= useSelector(selectDIAddTempResult);
    const submitResult = useSelector(selectDISubmitResult);
    const loading       = useSelector(selectDILoading);
    const errors         = useSelector(selectDIErrors);

    const [selectedCC, setSelectedCC] = useState('');

    const [category, setCategory] = useState('');
    const [selectionType, setSelectionType] = useState('');
    const [searchText, setSearchText] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchBoxRef = useRef(null);

    const [lotQtyEdits, setLotQtyEdits] = useState({});
    const [currentLotItemCode, setCurrentLotItemCode] = useState('');

    const [checkedRows, setCheckedRows] = useState({});
    const [issueDate, setIssueDate] = useState(todayFormatted());
    const [remarks, setRemarks] = useState('');

    useEffect(() => {
        if (uid && roleId) dispatch(fetchCostCenters({ uid, rid: roleId }));
        dispatch(fetchCategories());
        return () => dispatch(resetDailyIssue());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSuggestions(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    useEffect(() => {
        if (!selectedCC || !category || !selectionType || !searchText.trim()) {
            dispatch(clearSuggestions());
            return;
        }
        const t = setTimeout(() => {
            dispatch(fetchSuggestions({ pfx: searchText.trim(), typ: selectionType, cat: category, cc: selectedCC }));
            setShowSuggestions(true);
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, selectionType, category, selectedCC]);

    // Reset per-row lot qty edits whenever a fresh lot grid loads
    useEffect(() => {
        setLotQtyEdits({});
    }, [lotDetails]);

    // Reset per-row checkbox state whenever the main grid changes
    useEffect(() => {
        setCheckedRows({});
    }, [mainGridItems]);

    useEffect(() => {
        if (addTempResult) {
            toast.success(`Item Code :- '${addTempResult.code || currentLotItemCode}' Added Successfully`);
            dispatch(clearAddTempResult());
            setSearchText('');
            setShowSuggestions(false);
            if (selectedCC) dispatch(fetchMainGrid({ ccode: selectedCC, rid: roleId }));
        } else if (errors.addTemp) {
            toast.error(errors.addTemp);
            dispatch(clearAddTempResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addTempResult, errors.addTemp]);

    useEffect(() => {
        if (submitResult) {
            toast.success(`The Transaction Id Is :- ${submitResult.refNo}   Generated Successfully`);
            dispatch(clearSubmitResult());
            handleReset();
        } else if (errors.submit) {
            toast.error(errors.submit);
            dispatch(clearSubmitResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitResult, errors.submit]);

    // ── Cost Center selection ───────────────────────────────────────────────

    const handleCCChange = (cc) => {
        setSelectedCC(cc);
        setCategory(''); setSelectionType(''); setSearchText('');
        dispatch(clearSuggestions());
        dispatch(clearLotDetails());
        dispatch(clearMainGrid());
        if (cc) dispatch(fetchMainGrid({ ccode: cc, rid: roleId }));
    };

    // ── Item search / lot lookup ────────────────────────────────────────────

    const handleCategoryChange = (e) => {
        setCategory(e.target.value);
        setSelectionType('');
        setSearchText('');
        dispatch(clearSuggestions());
    };

    const handleSelectionTypeChange = (e) => {
        if (!category) { toast.error('Select Search By Category'); return; }
        setSelectionType(e.target.value);
        setSearchText('');
        dispatch(clearSuggestions());
    };

    const handlePickSuggestion = (s) => {
        setSearchText(s.SearchItem || '');
        setShowSuggestions(false);
    };

    const handleClearSearch = () => {
        setSearchText('');
        dispatch(clearSuggestions());
    };

    const handleViewLots = () => {
        if (!category)      { toast.error('Select Search By Category'); return; }
        if (!selectionType) { toast.error('Select Search By Type'); return; }
        if (!searchText.trim()) { toast.error('Enter Search Data'); return; }
        if (!selectedCC)     { toast.error('Select Cost Center'); return; }

        const itemCode = searchText.split(',')[0].trim();
        setCurrentLotItemCode(itemCode);
        dispatch(fetchLotDetails({ itemcode: itemCode, cccode: selectedCC }));
    };

    const handleHideLots = () => {
        dispatch(clearLotDetails());
        handleClearSearch();
    };

    // ── Lot grid qty edits ───────────────────────────────────────────────────

    const validateLotQty = (row, value) => {
        const avlQty = num(row.TempQuantity);
        const actQty = num(row.ACCQty);
        const v = num(value);
        if (row.Tblname === 'ClientMasterdata') {
            if (v !== avlQty) {
                toast.error('Invalid Quantity');
                return false;
            }
        } else {
            if (v > avlQty || v > actQty) {
                toast.error('Invalid Quantity');
                return false;
            }
        }
        return true;
    };

    const handleLotQtyChange = (row, value) => {
        if (value !== '' && !validateLotQty(row, value)) {
            setLotQtyEdits((prev) => ({ ...prev, [row.TempId]: '' }));
            return;
        }
        setLotQtyEdits((prev) => ({ ...prev, [row.TempId]: value }));
    };

    const lotAssignedTotal = useMemo(
        () => lotDetails.reduce((sum, row) => sum + num(lotQtyEdits[row.TempId]), 0),
        [lotDetails, lotQtyEdits]
    );

    const handleAddToIssue = () => {
        if (!selectedCC) { toast.error('Select Cost Center'); return; }

        const rowsToAdd = lotDetails.filter((row) => num(lotQtyEdits[row.TempId]) > 0);
        if (rowsToAdd.length === 0) {
            toast.error('Please Add Items to Issue');
            return;
        }
        for (const row of rowsToAdd) {
            if (!validateLotQty(row, lotQtyEdits[row.TempId])) return;
        }

        let Trowids = '', TItemCodes = '', TNewQtys = '', Ttblnames = '';
        rowsToAdd.forEach((row) => {
            Trowids    += `${row.TempId},`;
            TItemCodes += `${row.TempItemcode},`;
            TNewQtys   += `${num(lotQtyEdits[row.TempId])},`;
            Ttblnames  += `${row.Tblname},`;
        });

        dispatch(addTempIssueItems({
            Trowids, TItemCodes, TNewQtys, Ttblnames,
            TCCCode: selectedCC,
            TSingleItemcode: currentLotItemCode,
            RoleID: roleId,
            Createdby: currentUser,
        }));
    };

    // ── Main grid ────────────────────────────────────────────────────────────

    const toggleRowChecked = (idx) => {
        setCheckedRows((prev) => ({ ...prev, [idx]: !prev[idx] }));
    };

    const handleDeleteRow = (item, idx) => {
        if (!checkedRows[idx]) {
            toast.error('Please Check for Delete');
            return;
        }
        dispatch(deleteIssueItem({ ItemCode: item.ItemCode, FromCC: item.CCCode })).then((result) => {
            if (deleteIssueItem.fulfilled.match(result)) {
                toast.success('Deleted Successfully');
                dispatch(fetchMainGrid({ ccode: selectedCC, rid: roleId }));
            } else {
                toast.error(result.payload || 'Failed to delete item');
            }
        });
    };

    const handleSubmit = () => {
        if (!selectedCC) { toast.error('Select Cost Center'); return; }
        if (!issueDate)  { toast.error('Select Date'); return; }
        if (!remarks.trim()) { toast.error('Select Remarks'); return; }
        if (mainGridItems.length === 0) { toast.error('Please Add Daily Issue Items'); return; }

        const allChecked = mainGridItems.every((_, idx) => checkedRows[idx]);
        if (!allChecked) { toast.error('Please Verify Daily Issue Items'); return; }

        const allHaveQty = mainGridItems.every((item) => num(item.IssuedQty) > 0);
        if (!allHaveQty) { toast.error('Enter Quantity'); return; }

        let Qty = '', ItemCode = '', Amount = '';
        mainGridItems.forEach((item) => {
            Qty      += `${item.IssuedQty},`;
            ItemCode += `${item.ItemCode},`;
            Amount   += `${item.Amount},`;
        });

        dispatch(submitDailyIssue({
            Qty, ItemCode, Amount,
            Date: toApiDate(issueDate),
            Remarks: remarks,
            FromCC: selectedCC,
            Createdby: currentUser,
            RoleID: roleId,
        }));
    };

    const handleReset = () => {
        setSelectedCC('');
        setCategory(''); setSelectionType(''); setSearchText('');
        setLotQtyEdits({});
        setCurrentLotItemCode('');
        setCheckedRows({});
        setIssueDate(todayFormatted());
        setRemarks('');
        dispatch(clearMainGrid());
        dispatch(clearLotDetails());
        dispatch(clearSuggestions());
        if (uid && roleId) dispatch(fetchCostCenters({ uid, rid: roleId }));
    };

    // ── Render ───────────────────────────────────────────────────────────────

    const gridHasData = mainGridItems.length > 0;
    const checkedCount = Object.values(checkedRows).filter(Boolean).length;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 space-y-5">

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow">
                        <PackageMinus className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Daily Issue</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Issue stock items out of a cost center's lot-level inventory.</p>
                    </div>
                </div>
                <button onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>

            {/* ── Cost Center selection ─────────────────────────────────────── */}
            <SectionCard icon={Building2} title="Cost Center" accent="indigo">
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <Label required>Transfer Cost Center</Label>
                        <div className="relative">
                            <select className={inputCls} value={selectedCC} onChange={(e) => handleCCChange(e.target.value)}>
                                <option value="">-Please Select-</option>
                                {ccList.map((cc) => (
                                    <option key={cc.CCID} value={cc.CCID}>{cc.CCVAL}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                {loading.ccList ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                        </div>
                    </div>
                    {loading.mainGrid && (
                        <div className="flex items-center gap-2 text-sm text-indigo-600"><SpinnerIcon /> Loading items…</div>
                    )}
                </div>
            </SectionCard>

            {/* ── Item search ──────────────────────────────────────────────── */}
            {selectedCC && (
                <SectionCard icon={Search} title="Search &amp; Issue Item" accent="amber">
                    <div className="flex items-end gap-3 flex-wrap">
                        <div className="min-w-[200px]">
                            <Label required>Search By Category</Label>
                            <select className={inputCls} value={category} onChange={handleCategoryChange}>
                                <option value="">-Please Select-</option>
                                {categoryList.map((c) => (
                                    <option key={c.Categoryid} value={c.Categoryid}>{c.CategoryValue}</option>
                                ))}
                            </select>
                        </div>
                        <div className="min-w-[200px]">
                            <Label required>Search By Type</Label>
                            <select className={inputCls} value={selectionType} onChange={handleSelectionTypeChange} disabled={!category}>
                                <option value="">-Please Select-</option>
                                {SELECTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div className="relative flex-1 min-w-[260px]" ref={searchBoxRef}>
                            <Label required>Search Here</Label>
                            <input type="text" disabled={!selectionType} placeholder="Start typing to search…"
                                className={`${inputCls} uppercase`}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value.toUpperCase())}
                                onFocus={() => searchText && setShowSuggestions(true)} />
                            {loading.suggestions && (
                                <Loader2 className="absolute right-3 top-9 h-3.5 w-3.5 text-indigo-500 animate-spin" />
                            )}
                            {showSuggestions && suggestions.length > 0 && (
                                <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
                                    {suggestions.map((s, i) => (
                                        <button key={i} type="button" onClick={() => handlePickSuggestion(s)}
                                            className="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-b border-gray-50 dark:border-gray-700 last:border-0">
                                            {s.SearchItem}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={handleViewLots} disabled={loading.lotDetails}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loading.lotDetails ? <SpinnerIcon /> : <Search className="w-4 h-4" />} View
                            </button>
                            <button onClick={handleClearSearch}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                                <X className="w-3.5 h-3.5" /> Clear
                            </button>
                        </div>
                    </div>
                </SectionCard>
            )}

            {/* ── Lot / temp grid ──────────────────────────────────────────── */}
            {lotDetails.length > 0 && (
                <SectionCard icon={Layers} title="Added Items"
                    subtitle={`Assign quantity per lot — Total: ${lotAssignedTotal.toFixed(4)}`}
                    accent="purple">
                    <div className="overflow-x-auto rounded-lg border border-purple-200 dark:border-purple-700">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                                    {['Received Date', 'Item Code', 'Mrr/Ref No', 'PO/Indent No', 'Quantity', 'Assigned Qty'].map((h) => (
                                        <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {lotDetails.map((row, idx) => (
                                    <tr key={row.TempId ?? idx} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-purple-50/40 dark:bg-purple-900/10'}>
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.Tempdate}</td>
                                        <td className="px-2.5 py-2 whitespace-nowrap font-mono text-gray-800 dark:text-gray-200">{row.TempItemcode}</td>
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.Tempmrr}</td>
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.Temppono}</td>
                                        <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{row.TempQuantity}</td>
                                        <td className="px-1.5 py-1.5">
                                            <input type="number" min="0" step="0.0001"
                                                className={`${inputCls} w-24 text-right`}
                                                value={lotQtyEdits[row.TempId] ?? ''}
                                                onChange={(e) => setLotQtyEdits((prev) => ({ ...prev, [row.TempId]: e.target.value }))}
                                                onBlur={(e) => handleLotQtyChange(row, e.target.value)} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end gap-2">
                        <button onClick={handleHideLots}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                            Hide
                        </button>
                        <button onClick={handleAddToIssue} disabled={loading.addTemp}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50">
                            {loading.addTemp ? <SpinnerIcon /> : <Plus className="w-4 h-4" />} Add
                        </button>
                    </div>
                </SectionCard>
            )}

            {/* ── Main staged-items grid ───────────────────────────────────── */}
            {gridHasData && (
                <SectionCard icon={Layers} title="Daily Issue Items"
                    subtitle={`${checkedCount} of ${mainGridItems.length} item${mainGridItems.length !== 1 ? 's' : ''} verified`}
                    accent="indigo">
                    <div className="overflow-x-auto rounded-lg border border-indigo-200 dark:border-indigo-700">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
                                    {['Chk', 'Item Code', 'Item Name', 'Specification', 'DCA', 'Sub DCA', 'Units', 'Basic', 'Available Qty', 'Issued Qty', ''].map((h) => (
                                        <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {mainGridItems.map((item, idx) => (
                                    <tr key={`${item.ItemCode}_${idx}`} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-indigo-50/40 dark:bg-indigo-900/10'}>
                                        <td className="px-2.5 py-2">
                                            <input type="checkbox" className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                                checked={!!checkedRows[idx]} onChange={() => toggleRowChecked(idx)} />
                                        </td>
                                        <td className="px-2.5 py-2 whitespace-nowrap font-mono text-gray-800 dark:text-gray-200">{item.ItemCode}</td>
                                        <td className="px-2.5 py-2 text-gray-900 dark:text-white">{item.ItemName}</td>
                                        <td className="px-2.5 py-2 text-gray-600 dark:text-gray-400">{item.Specification}</td>
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{item.DcaCode}</td>
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{item.SubDCAcode}</td>
                                        <td className="px-2.5 py-2 text-gray-700 dark:text-gray-300">{item.Units}</td>
                                        <td className="px-2.5 py-2 text-right text-gray-700 dark:text-gray-300">{item.Basic}</td>
                                        <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{item.AvailableQty}</td>
                                        <td className="px-2.5 py-2 text-right font-semibold text-indigo-700 dark:text-indigo-400">{item.IssuedQty}</td>
                                        <td className="px-1.5 py-1.5">
                                            <button onClick={() => handleDeleteRow(item, idx)} disabled={loading.deleteItem}
                                                className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 disabled:opacity-50">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label required>Issued Date</Label>
                            <CustomDatePicker value={issueDate} onChange={setIssueDate}
                                format="DD-MMM-YYYY" placeholder="Select Issued Date" maxDate={todayFormatted()} />
                        </div>
                        <div>
                            <Label required>Remarks</Label>
                            <textarea rows={2} className={inputCls} placeholder="Enter remarks…"
                                value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button onClick={handleReset}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                            Reset
                        </button>
                        <button onClick={handleSubmit} disabled={loading.submit}
                            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                            {loading.submit ? <SpinnerIcon /> : <Send className="w-4 h-4" />}
                            {loading.submit ? 'Submitting…' : 'Submit'}
                        </button>
                    </div>
                </SectionCard>
            )}
        </div>
    );
};

export default DailyIssue;
